import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Col, Form, Input, Row, Select } from "antd";
import debounce from "lodash.debounce";
import moment from "moment";
import {
  BOOKING_CARTON_LIST,
  CARTON_INVENTORY_CREATE,
  CHALLAN_LIST,
  GET_BUYER_ENDPOINT,
  GET_LOCAL_SUPPLIER_LISTS,
  GET_ORGANIZATION_ENDPOINT,
} from "../../../apiServices/API_ENDPOINTS";
import { getData } from "../../../apiServices/common";
import { incomingMovementTypes, outgoingMovementTypes } from "../../../common/helper/helper";
import CustomDatePicker from "common/CustomDatePicker";
import { isArrayAndHasValue } from "../../../utils/functions";
import HandleNotification from "../../../common/Notification";

const CARTON_MOVEMENT_TYPES = [
  incomingMovementTypes.find((m) => m.value === "receive_from_supplier"),
  outgoingMovementTypes.find((m) => m.value === "issue_to_packing"),
].filter(Boolean);

const ORGANIZATION_DROPDOWN_PAGE_SIZE = 500;

const CartonInventoryForm = ({ form, onBookingDataLoaded, onBookingDataCleared }) => {
  const [supplierList, setSupplierList] = useState([]);
  const [buyerList, setBuyerList] = useState([]);
  const [buyerLoading, setBuyerLoading] = useState(false);
  const [supplierPoList, setSupplierPoList] = useState([]);
  const [supplierPoLoading, setSupplierPoLoading] = useState(false);
  const [organizationList, setOrganizationList] = useState([]);
  const [challanList, setChallanList] = useState([]);
  const [bookingLoading, setBookingLoading] = useState(false);

  const watchedBuyerId = Form.useWatch("buyer_id", form);
  const watchedSupplierId = Form.useWatch("supplier_id", form);
  const watchedMovement = Form.useWatch("movement_type", form);

  const fetchSuppliers = useCallback(async () => {
    const response = await getData(GET_LOCAL_SUPPLIER_LISTS, false, {});
    if (response?.data?.code === 200) {
      setSupplierList(response?.data?.data || []);
    }
  }, []);

  const fetchBuyerList = useCallback(async (filterValues = {}) => {
    setBuyerLoading(true);
    const response = await getData(GET_BUYER_ENDPOINT, false, { per_page: 10, ...filterValues });
    if (response?.data?.code === 200) {
      setBuyerList(response?.data?.data?.data || []);
    }
    setBuyerLoading(false);
  }, []);

  const fetchOrganizationList = useCallback(async (filterValues = {}) => {
    const response = await getData(GET_ORGANIZATION_ENDPOINT, false, {
      per_page: ORGANIZATION_DROPDOWN_PAGE_SIZE,
      ...filterValues,
    });
    if (response?.data?.code === 200) {
      setOrganizationList(response?.data?.data?.data || []);
    }
  }, []);

  const fetchChallanList = useCallback(async (filterValues = {}) => {
    const response = await getData(CHALLAN_LIST, false, { per_page: 1000, ...filterValues });
    if (response?.data?.code === 200) {
      setChallanList(response?.data?.data?.data || response?.data?.data || []);
    }
  }, []);

  const fetchSupplierPoList = useCallback(async (buyerId, supplierId) => {
    if (!buyerId || !supplierId) {
      setSupplierPoList([]);
      return;
    }
    setSupplierPoLoading(true);
    const response = await getData(BOOKING_CARTON_LIST, false, {
      page: 1,
      buyer_id: buyerId,
      supplier_id: supplierId,
    });
    if (response?.data?.code === 200) {
      const payload = response?.data?.data;
      setSupplierPoList(Array.isArray(payload) ? payload : payload?.data || []);
    } else {
      setSupplierPoList([]);
    }
    setSupplierPoLoading(false);
  }, []);

  const loadBookingDetails = useCallback(
    async (bookingCartonId) => {
      if (!bookingCartonId) return;
      setBookingLoading(true);
      const response = await getData(CARTON_INVENTORY_CREATE(bookingCartonId));
      if (response?.data?.code === 200) {
        const data = response?.data?.data;

        let deliveryAddress;
        if (data?.delivery_to) {
          const org = organizationList.find((o) => o.name === data.delivery_to);
          if (org?.address) {
            deliveryAddress = org.address;
          } else {
            const orgResponse = await getData(GET_ORGANIZATION_ENDPOINT, false, {
              name: data.delivery_to,
              per_page: 1,
            });
            deliveryAddress = orgResponse?.data?.data?.data?.[0]?.address;
          }
        }

        form.setFieldsValue({
          booking_date: data?.booking_date ? moment(data.booking_date) : null,
          supplier_name: data?.supplier?.name,
          buyer_name: data?.buyer?.name,
          contract_person: data?.contract_person,
          contract_person_address: data?.contract_person_address,
          contract_person_phone: data?.contract_person_phone,
          delivery_to: data?.delivery_to,
          delivery_address: deliveryAddress,
          cgl_contract_person: data?.cgl_poc?.name,
          cgl_contract_person_phone: data?.cgl_poc_phone,
        });

        if (data?.buyer) {
          setBuyerList((prev) => (prev.some((b) => b.id === data.buyer.id) ? prev : [data.buyer, ...prev]));
        }

        onBookingDataLoaded?.({
          breakdowns: data?.breakdowns || [],
          supportingMaterials: data?.supporting_materials || [],
          bookingCartonId,
        });
      } else {
        HandleNotification("error", "bottomRight", "Failed to load booking details", null);
        onBookingDataCleared?.();
      }
      setBookingLoading(false);
    },
    [form, organizationList, onBookingDataLoaded, onBookingDataCleared],
  );

  useEffect(() => {
    fetchSuppliers();
    fetchBuyerList({ per_page: 10 });
    fetchOrganizationList();
    fetchChallanList();
  }, [fetchSuppliers, fetchBuyerList, fetchOrganizationList, fetchChallanList]);

  useEffect(() => {
    if (watchedBuyerId && watchedSupplierId) {
      fetchSupplierPoList(watchedBuyerId, watchedSupplierId);
    } else {
      setSupplierPoList([]);
    }
  }, [watchedBuyerId, watchedSupplierId, fetchSupplierPoList]);

  const debouncedFetchBuyers = useMemo(
    () => debounce((name) => fetchBuyerList(name ? { name } : { per_page: 10 }), 400),
    [fetchBuyerList],
  );

  const debouncedFetchOrganizations = useMemo(
    () =>
      debounce(
        (name) =>
          fetchOrganizationList(
            name ? { name, per_page: 10 } : { per_page: ORGANIZATION_DROPDOWN_PAGE_SIZE },
          ),
        400,
      ),
    [fetchOrganizationList],
  );

  const debouncedFetchChallans = useMemo(
    () => debounce((name) => fetchChallanList(name ? { name } : { per_page: 1000 }), 400),
    [fetchChallanList],
  );

  const onSupplierPoChange = (bookingCartonId) => {
    if (!bookingCartonId) {
      onBookingDataCleared?.();
      return;
    }
    loadBookingDetails(bookingCartonId);
  };

  const onBuyerSupplierChange = () => {
    form.setFieldsValue({
      booking_carton_id: undefined,
      booking_date: undefined,
      supplier_name: undefined,
      buyer_name: undefined,
      contract_person: undefined,
      contract_person_address: undefined,
      contract_person_phone: undefined,
      delivery_to: undefined,
      delivery_address: undefined,
      cgl_contract_person: undefined,
      cgl_contract_person_phone: undefined,
    });
    onBookingDataCleared?.();
  };

  const onDeliveryOrganizationChange = (orgName) => {
    const org = organizationList.find((o) => o.name === orgName);
    form.setFieldsValue({ delivery_address: org?.address ?? undefined });
  };

  const showChallan = watchedMovement === "receive_from_supplier";
  const showRequisition = watchedMovement === "issue_to_packing";

  useEffect(() => {
    if (!showChallan) {
      form.setFieldsValue({ challan_id: undefined });
    }
  }, [showChallan, form]);

  useEffect(() => {
    if (!showRequisition) {
      form.setFieldsValue({ requisition_no: undefined });
    }
  }, [showRequisition, form]);

  return (
    <Card title="Carton Inventory Entry">
      <Row gutter={[12, 0]}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item
            label="Buyer"
            name="buyer_id"
            rules={[{ required: true, message: "Buyer is required" }]}
          >
            <Select
              showSearch
              allowClear
              placeholder="Search buyer"
              loading={buyerLoading}
              filterOption={false}
              onSearch={debouncedFetchBuyers}
              onClear={() => fetchBuyerList({ per_page: 10 })}
              onChange={onBuyerSupplierChange}
              options={buyerList.map((b) => ({ label: b.name, value: b.id }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item
            label="Supplier"
            name="supplier_id"
            rules={[{ required: true, message: "Supplier is required" }]}
          >
            <Select
              showSearch
              allowClear
              placeholder="Select supplier"
              filterOption={(input, option) =>
                (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase())
              }
              onChange={onBuyerSupplierChange}
              options={supplierList.map((s) => ({ label: s.name, value: s.id }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item
            label="Supplier PO"
            name="booking_carton_id"
            rules={[{ required: true, message: "Supplier PO is required" }]}
          >
            <Select
              showSearch
              allowClear
              placeholder={watchedBuyerId && watchedSupplierId ? "Select supplier PO" : "Select buyer & supplier first"}
              loading={supplierPoLoading || bookingLoading}
              disabled={!watchedBuyerId || !watchedSupplierId || bookingLoading}
              onChange={onSupplierPoChange}
              filterOption={(input, option) =>
                (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase())
              }
              options={supplierPoList.map((po) => ({
                label: po.supplier_po_number,
                value: po.id,
              }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item label="Booking Date" name="booking_date">
            <CustomDatePicker style={{ width: "100%" }} size="small" disabled />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item label="Supplier" name="supplier_name">
            <Input disabled placeholder="Supplier" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item label="Buyer" name="buyer_name">
            <Input disabled placeholder="Buyer" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item label="Contact Person (Supplier)" name="contract_person">
            <Input disabled placeholder="Contact Person (Supplier)" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item label="Address" name="contract_person_address">
            <Input disabled placeholder="Address" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item label="Contact Person Phone (Supplier)" name="contract_person_phone">
            <Input disabled placeholder="Phone (Supplier)" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item label="Delivery To" name="delivery_to">
            <Select
              showSearch
              allowClear
              placeholder="Delivery To"
              disabled
              filterOption={false}
              onSearch={debouncedFetchOrganizations}
              onChange={onDeliveryOrganizationChange}
              options={organizationList.map((org) => ({ label: org.name, value: org.name }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item label="Delivery Address" name="delivery_address">
            <Input disabled placeholder="Delivery Address" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item label="Contact Person (CGL)" name="cgl_contract_person">
            <Input disabled placeholder="Contact Person (CGL)" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item label="Contact Person Phone (CGL)" name="cgl_contract_person_phone">
            <Input disabled placeholder="Phone (CGL)" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Form.Item
            label="Movement Type"
            name="movement_type"
            rules={[{ required: true, message: "Movement type is required" }]}
          >
            <Select
              allowClear
              placeholder="Select movement type"
              options={CARTON_MOVEMENT_TYPES.map((m) => ({ label: m.label, value: m.value }))}
            />
          </Form.Item>
        </Col>
        {(showChallan || showRequisition) && (
          <Col xs={24} sm={12} md={8} lg={6}>
            {showChallan && (
              <Form.Item
                label="Challan No"
                name="challan_id"
                rules={[{ required: true, message: "Challan No is required" }]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Search challan"
                  filterOption={false}
                  onSearch={debouncedFetchChallans}
                  onClear={() => fetchChallanList({ per_page: 1000 })}
                >
                  {isArrayAndHasValue(challanList) &&
                    challanList.map((challan) => (
                      <Select.Option key={challan.id} value={challan.id}>
                        {challan.challan_no || challan.name || `#${challan.id}`}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            )}
            {showRequisition && (
              <Form.Item
                label="Requisition No"
                name="requisition_no"
                rules={[{ required: true, message: "Requisition No is required" }]}
              >
                <Input placeholder="Requisition No" />
              </Form.Item>
            )}
          </Col>
        )}
      </Row>
    </Card>
  );
};

export default CartonInventoryForm;
